<template>
  <div class="reset-password-page">
    
    <div class="skip-links">
      <a href="#main-content" class="skip-link">Zum Hauptinhalt</a>
    </div>

    <div class="auth-container">
      <div class="logo-container">
        <h1 class="app-title">Quitly</h1>
      </div>
      <h2 id="main-content" class="page-title">Passwort zurücksetzen</h2>
      
      <div v-if="successMessage" class="alert alert-success">
        {{ successMessage }}
      </div>
      
      <div v-if="errorMessage" class="alert alert-danger">
        {{ errorMessage }}
      </div>
      <div class="alert alert-info">
        <strong>Hinweis:</strong> Nach dem Zurücksetzen deines Passworts wirst du auf allen Geräten ausgeloggt und musst dich erneut anmelden.
      </div>

      <div v-if="!resetComplete">
        <p class="instruction-text">
          Bitte gib dein neues Passwort ein
        </p>

        <form @submit.prevent="handleResetPassword" class="auth-form">
          <div class="mb-3">
            <label for="newPassword" class="form-label d-flex align-items-center">Neues Passwort <span class="badge bg-danger ms-2">Pflichtfeld</span></label>
            <div class="input-group">
              <input
                :type="showPassword ? 'text' : 'password'"
                class="form-control"
                id="newPassword"
                v-model="password"
                :placeholder="'Neues Passwort eingeben'"
                minlength="8"
                @input="onPasswordChange"
                :class="{ 'is-invalid': passwordError }"
                required
                autocomplete="new-password"
              />
              <button
                type="button"
                class="btn btn-outline-secondary"
                @click="togglePasswordVisibility"
              >
                <font-awesome-icon :icon="showPassword ? 'eye-slash' : 'eye'" />
              </button>
            </div>
            <div v-if="passwordError" class="invalid-feedback d-block">
              {{ passwordError }}
            </div>

            
            <div v-if="password" class="mt-2">
              <div class="d-flex justify-content-between">
                <small>Passwortstärke:</small>
                <small :class="getTextColorClass(passwordStrength.class)">
                  {{ passwordStrength.class === 'weak' ? 'Schwach' :
                     passwordStrength.class === 'medium' ? 'Mittel' :
                     passwordStrength.class === 'strong' ? 'Gut' :
                     passwordStrength.class === 'veryStrong' ? 'Sehr stark' : '' }}
                </small>
              </div>
              <div class="progress quitly-progress-thin">
                <div
                  class="progress-bar quitly-progress-animated"
                  :class="getProgressBarClass(passwordStrength.class)"
                  role="progressbar"
                  :style="{ width: passwordStrength.percent + '%' }"
                  :aria-valuenow="passwordStrength.percent"
                  aria-valuemin="0"
                  aria-valuemax="100"
                ></div>
              </div>
              <div class="mt-1">
                <small>Passwortanforderungen</small>
              </div>
            </div>
          </div>
          
          <div class="mb-3">
            <label for="confirmPassword" class="form-label d-flex align-items-center">Passwort bestätigen <span class="badge bg-danger ms-2">Pflichtfeld</span></label>
            <div class="input-group">
              <input
                :type="showConfirmPassword ? 'text' : 'password'"
                class="form-control"
                id="confirmPassword"
                v-model="confirmPassword"
                :placeholder="'Neues Passwort bestätigen'"
                @input="validateConfirmPassword"
                :class="{ 'is-invalid': confirmPasswordError }"
                required
                autocomplete="new-password"
              />
              <button
                type="button"
                class="btn btn-outline-secondary"
                @click="toggleConfirmPasswordVisibility"
              >
                <font-awesome-icon :icon="showConfirmPassword ? 'eye-slash' : 'eye'" />
              </button>
            </div>
            <div v-if="confirmPasswordError" class="invalid-feedback d-block">
              {{ confirmPasswordError }}
            </div>
          </div>

          <button type="submit" class="btn btn-primary btn-block" :disabled="isLoading || !isValid">
            <span v-if="isLoading">Wird verarbeitet...</span>
            <span v-else>Passwort zurücksetzen</span>
          </button>
        </form>
      </div>

      <div v-else class="success-container">
        <div class="success-icon">
          <font-awesome-icon icon="check-circle" size="4x" class="text-success" />
        </div>
        <p class="success-text">
          Ihr Passwort wurde erfolgreich zurückgesetzt
        </p>
        <div class="mt-4">
          <router-link to="/auth/login" class="btn btn-primary">
            {{ 'Zur Anmeldung' }}
          </router-link>
        </div>
      </div>

      <div class="auth-footer">
        <p>
          {{ 'Passwort wieder eingefallen?' }}
          <router-link to="/auth/login">{{ 'Anmelden' }}</router-link>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { resetPassword } from '@/api/auth'

const route = useRoute()
const router = useRouter()
const token = ref('')

const password = ref('')
const passwordError = ref('')
const confirmPassword = ref('')
const confirmPasswordError = ref('')
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const isLoading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')
const resetComplete = ref(false)
const passwordStrength = ref({
  class: 'none',
  percent: 0
})

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}

const toggleConfirmPasswordVisibility = () => {
  showConfirmPassword.value = !showConfirmPassword.value
}

const validatePassword = () => {
  passwordError.value = ''
  const password_val = password.value
  
  if (!password_val) {
    passwordStrength.value = { class: 'none', percent: 0 }
    return
  }
  
  if (password_val.length < 8) {
    passwordError.value = 'Passwort muss mindestens 8 Zeichen lang sein'
    passwordStrength.value = { class: 'weak', percent: 25 }
    return
  }

  let strength = 0

  if (password_val.length >= 8) strength += 1
  if (password_val.length >= 12) strength += 1

  if (/[A-Z]/.test(password_val)) strength += 1
  if (/[a-z]/.test(password_val)) strength += 1
  if (/[0-9]/.test(password_val)) strength += 1
  if (/[^A-Za-z0-9]/.test(password_val)) strength += 1

  if (strength <= 2) {
    passwordStrength.value = { class: 'weak', percent: 25 }
  } else if (strength <= 4) {
    passwordStrength.value = { class: 'medium', percent: 50 }
  } else if (strength <= 5) {
    passwordStrength.value = { class: 'strong', percent: 75 }
  } else {
    passwordStrength.value = { class: 'veryStrong', percent: 100 }
  }
}

const validateConfirmPassword = () => {
  confirmPasswordError.value = ''
  
  if (confirmPassword.value && password.value !== confirmPassword.value) {
    confirmPasswordError.value = 'Die Passwörter stimmen nicht überein'
  }
}

const onPasswordChange = () => {
  validatePassword()
  if (confirmPassword.value) {
    validateConfirmPassword()
  }
}

const getProgressBarClass = (strengthClass) => {
  switch (strengthClass) {
    case 'weak': return 'bg-danger'
    case 'medium': return 'bg-warning'
    case 'strong': return 'bg-info'
    case 'veryStrong': return 'bg-success'
    default: return ''
  }
}

const getTextColorClass = (strengthClass) => {
  switch (strengthClass) {
    case 'weak': return 'text-danger'
    case 'medium': return 'text-warning'
    case 'strong': return 'text-info'
    case 'veryStrong': return 'text-success'
    default: return ''
  }
}

const isValid = computed(() => {
  return password.value.length >= 8 && 
         password.value === confirmPassword.value && 
         passwordStrength.value.class !== 'weak'
})

onMounted(() => {
  const queryToken = route.query.token
  if (queryToken) {
    token.value = queryToken
  } else {
    errorMessage.value = 'Token fehlt'
  }
})

const handleResetPassword = async () => {
  if (!token.value) {
    errorMessage.value = 'Token fehlt'
    return
  }

  passwordError.value = ''
  confirmPasswordError.value = ''
  errorMessage.value = ''

  validatePassword()
  validateConfirmPassword()
  
  if (passwordError.value || confirmPasswordError.value) {
    return
  }
  
  if (!password.value) {
    passwordError.value = 'Passwort ist erforderlich'
    return false
  }
  
  if (password.value !== confirmPassword.value) {
    confirmPasswordError.value = 'Die Passwörter stimmen nicht überein'
    return false
  }

  try {
    isLoading.value = true

    await resetPassword(token.value, password.value)

    resetComplete.value = true
    successMessage.value = 'Passwort erfolgreich zurückgesetzt'


    setTimeout(() => {
      password.value = ''
      confirmPassword.value = ''
      router.push('/auth/login')
    }, 3000)
  } catch (error) {
    console.error('Fehler beim Passwort zurücksetzen:', error)
    errorMessage.value = error.response?.data?.message || 'Passwort-Reset fehlgeschlagen'
  } finally {
    isLoading.value = false
  }
}
</script>
